/**
 * React hook for live projection.
 * Connects assumptions state to the projection engine.
 * Re-projects on every assumption change (instant, no debounce needed -- pure math).
 */

import { useState, useMemo, useCallback } from 'react'
import {
  project,
  cashForecast13Week,
  BaseActuals,
  Assumptions,
  ProjectionResult,
} from '../services/projectionEngine'

const DEFAULT_ASSUMPTIONS: Assumptions = {
  revenue_growth_rate: 5.0,
  cogs_pct_of_revenue: 65.0,
  opex_growth_rate: 3.0,
  headcount_change: 0,
  cost_per_head: 75000,
  collection_days: 45,
  payment_terms: 30,
  interest_rate: 5.5,
  tax_rate: 25.0,
  capex: 0,
  depreciation: 0,
}

export function useProjection(baseActuals: BaseActuals | null) {
  const [assumptions, setAssumptions] = useState<Assumptions>(DEFAULT_ASSUMPTIONS)

  const projection = useMemo<ProjectionResult | null>(() => {
    if (!baseActuals) return null
    return project(baseActuals, assumptions)
  }, [baseActuals, assumptions])

  const cashForecast = useMemo(() => {
    if (!baseActuals) return []
    return cashForecast13Week(baseActuals, assumptions)
  }, [baseActuals, assumptions])

  const updateAssumption = useCallback((key: keyof Assumptions, value: number) => {
    setAssumptions(prev => ({ ...prev, [key]: value }))
  }, [])

  const resetAssumptions = useCallback(() => {
    setAssumptions(DEFAULT_ASSUMPTIONS)
  }, [])

  const loadAssumptions = useCallback((loaded: Partial<Assumptions>) => {
    setAssumptions(prev => ({ ...prev, ...loaded }))
  }, [])

  return {
    assumptions,
    projection,
    cashForecast,
    updateAssumption,
    resetAssumptions,
    loadAssumptions,
    setAssumptions,
  }
}
